'use client'

import pick from 'lodash/pick'
import { useSearchParams } from 'next/navigation'
import Script from 'next/script'
import { useMemo, useRef } from 'react'

import { configPublic } from '@src/config.public'
import { useSession } from '@src/hooks/useSession'
import useGeolocationStore from '@src/stores/geolocationStore'

import { getBuilderPreviewMode } from './builder.util'
import { getIsServer } from './common.util'
import { QUERY_PARAMS } from './constants.util'

function useCheckGTMDisabled() {
  const searchParams = useSearchParams()

  const isGTMDisabled = useMemo(() => {
    const codeFromQuery = searchParams?.get(QUERY_PARAMS.gtmDisabled)
    return codeFromQuery === 'true'
  }, [searchParams])

  return isGTMDisabled
}

export function GoogleTagManager() {
  const { edit } = getBuilderPreviewMode()
  const isGTMDisabled = useCheckGTMDisabled()
  const geoLocation = useGeolocationStore((state) => state.geoLocation)
  const didTriggerGeo = useRef(false)
  const { commercetoolsSession } = useSession()

  if (edit || isGTMDisabled) return null

  if (!getIsServer()) {
    window.dataLayer = window.dataLayer || []
  }

  if (!didTriggerGeo.current && !getIsServer() && geoLocation) {
    didTriggerGeo.current = true
    dataLayer.unshift({
      event: 'geo',
      geoLocation: pick(geoLocation, 'country', 'countryRegion'),
      external_id: commercetoolsSession?.anonymousId,
    })
  }

  // NOTE: We use a ref to keep track of when the geo event was sent in order to
  // ensure we send the geo event before GTM is initialized.
  if (!didTriggerGeo.current) return null

  const { id, auth, previewEnv } = configPublic.googleTagManager

  const gtmBaseUrl = 'https://www.googletagmanager.com'
  const iframeUrl = new URL('ns.html', gtmBaseUrl)
  const scriptUrl = new URL('gtm.js', gtmBaseUrl)
  iframeUrl.searchParams.set('id', id)
  scriptUrl.searchParams.set('id', id)

  if (auth && previewEnv) {
    iframeUrl.searchParams.set('gtm_auth', auth)
    iframeUrl.searchParams.set('gtm_preview', previewEnv)
    iframeUrl.searchParams.set('gtm_cookies_win', 'x')
    scriptUrl.searchParams.set('gtm_auth', auth)
    scriptUrl.searchParams.set('gtm_preview', previewEnv)
    scriptUrl.searchParams.set('gtm_cookies_win', 'x')
  }

  return (
    <>
      <noscript>
        <iframe
          title="google-tag-manager"
          src={iframeUrl.toString()}
          height="0"
          width="0"
          style={{ display: 'none', visibility: 'hidden' }}
        />
      </noscript>
      <Script
        id="gtm-script"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
    (function(w,d,s,l){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    '${scriptUrl.toString()}';f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer');
  `,
        }}
      />
    </>
  )
}
